import { LIST_GEO_COUNTRIES } from '@wiwodesk/graphql-schema';
import type { Country } from '@wiwodesk/shared-domain';
import { toDomain, type CountryRecord } from '@wiwodesk/shared-domain';

import { assertNoGraphqlErrors, executeCustomGraphql } from '@/services/graphql/graphqlClient';

export type ListGeoCountriesApiResponse = {
  items?: unknown;
};

function normalizeItems(rawItems: unknown): CountryRecord[] {
  let parsed: unknown = rawItems;
  /** Legacy path: Amplify/AWSJSON sometimes returned geo rows as a JSON string; prefer structured `items` arrays. */
  if (typeof rawItems === 'string') {
    try {
      parsed = JSON.parse(rawItems) as unknown;
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.filter(
    (row): row is CountryRecord =>
      row !== null &&
      typeof row === 'object' &&
      'code' in row &&
      'name' in row &&
      typeof (row as { code: unknown }).code === 'string' &&
      typeof (row as { name: unknown }).name === 'string'
  );
}

/**
 * Fetches countries via custom AppSync query `listGeoCountries` (geo-reference Lambda → shared refdata DynamoDB).
 */
export async function fetchCountries(filter?: { activeOnly?: boolean }): Promise<Country[]> {
  const result = await executeCustomGraphql<{
    listGeoCountries?: ListGeoCountriesApiResponse | null;
  }>({
    query: LIST_GEO_COUNTRIES,
    variables: {
      activeOnly: filter?.activeOnly ?? undefined,
    },
  });
  assertNoGraphqlErrors('listGeoCountries', result.errors);

  const raw = result.data as
    | { listGeoCountries?: ListGeoCountriesApiResponse }
    | ListGeoCountriesApiResponse
    | null
    | undefined;
  const payload: ListGeoCountriesApiResponse | undefined =
    raw != null &&
    typeof raw === 'object' &&
    'listGeoCountries' in raw &&
    raw.listGeoCountries != null
      ? raw.listGeoCountries
      : (raw as ListGeoCountriesApiResponse | undefined);

  const items = normalizeItems(payload?.items);

  return items.map(toDomain);
}
